import { useState, useRef, useEffect } from "react";
import { 
  Copy, 
  Clipboard, 
  Scissors, 
  Trash2, 
  Maximize2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Bold,
  Italic,
  Strikethrough,
  Type,
  Layers,
  ChevronUp,
  ChevronDown
} from "lucide-react";

/**
 * ElementContextMenu - Floating context menu for canvas elements
 * Shows on right-click or when clicking the menu button on element border
 */
export function ElementContextMenu({
  position,
  element,
  onClose,
  onCopy,
  onCut,
  onPaste,
  onDelete,
  onDuplicate,
  onFitToWidth,
  onBringForward,
  onSendBackward,
  onUpdateElement,
  canvasWidth,
}) {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  if (!position) return null;

  const isTextElement = element?.type === "text" || element?.type === "pedagogical_text";

  const menuItems = [
    { icon: Copy, label: "Copier", action: onCopy, shortcut: "Ctrl+C" },
    { icon: Scissors, label: "Couper", action: onCut, shortcut: "Ctrl+X" },
    { icon: Clipboard, label: "Coller", action: onPaste, shortcut: "Ctrl+V" },
    { divider: true },
    { icon: Layers, label: "Dupliquer", action: onDuplicate, shortcut: "Ctrl+D" },
    { divider: true },
    { icon: Maximize2, label: "Ajuster a la largeur", action: onFitToWidth },
    { divider: true },
    { icon: ChevronUp, label: "Avancer", action: onBringForward },
    { icon: ChevronDown, label: "Reculer", action: onSendBackward },
    { divider: true },
    { icon: Trash2, label: "Supprimer", action: onDelete, danger: true, shortcut: "Suppr" },
  ];

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 min-w-[200px]"
      style={{
        left: Math.min(position.x, window.innerWidth - 220),
        top: Math.min(position.y, window.innerHeight - 400),
      }}
    >
      {/* Text-specific options */}
      {isTextElement && (
        <>
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="text-xs font-bold text-slate-400 uppercase mb-2">Style de texte</p>
            <div className="flex gap-1">
              <button
                onClick={() => onUpdateElement("fontWeight", element.fontWeight === "bold" ? "normal" : "bold")}
                className={`p-2 rounded-lg transition-colors ${
                  element.fontWeight === "bold" ? "bg-slate-800 text-white" : "bg-slate-100 hover:bg-slate-200"
                }`}
                title="Gras"
              >
                <Bold size={16} />
              </button>
              <button
                onClick={() => onUpdateElement("fontStyle", element.fontStyle === "italic" ? "normal" : "italic")}
                className={`p-2 rounded-lg transition-colors ${
                  element.fontStyle === "italic" ? "bg-slate-800 text-white" : "bg-slate-100 hover:bg-slate-200"
                }`}
                title="Italique"
              >
                <Italic size={16} />
              </button>
              <button
                onClick={() => onUpdateElement("textDecoration", element.textDecoration === "line-through" ? "none" : "line-through")}
                className={`p-2 rounded-lg transition-colors ${
                  element.textDecoration === "line-through" ? "bg-slate-800 text-white" : "bg-slate-100 hover:bg-slate-200"
                }`}
                title="Barre"
              >
                <Strikethrough size={16} />
              </button>
            </div>
          </div>
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="text-xs font-bold text-slate-400 uppercase mb-2">Alignement</p>
            <div className="flex gap-1">
              <button
                onClick={() => onUpdateElement("align", "left")}
                className={`p-2 rounded-lg transition-colors ${
                  (element.align || "left") === "left" ? "bg-slate-800 text-white" : "bg-slate-100 hover:bg-slate-200"
                }`}
              >
                <AlignLeft size={16} />
              </button>
              <button
                onClick={() => onUpdateElement("align", "center")}
                className={`p-2 rounded-lg transition-colors ${
                  element.align === "center" ? "bg-slate-800 text-white" : "bg-slate-100 hover:bg-slate-200"
                }`}
              >
                <AlignCenter size={16} />
              </button>
              <button
                onClick={() => onUpdateElement("align", "right")}
                className={`p-2 rounded-lg transition-colors ${
                  element.align === "right" ? "bg-slate-800 text-white" : "bg-slate-100 hover:bg-slate-200"
                }`}
              >
                <AlignRight size={16} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* General menu items */}
      {menuItems.map((item, index) => {
        if (item.divider) {
          return <div key={index} className="border-t border-slate-100 my-1" />;
        }
        const Icon = item.icon;
        return (
          <button
            key={index}
            onClick={() => { item.action?.(); onClose(); }}
            className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-50 transition-colors ${
              item.danger ? "text-red-600 hover:bg-red-50" : "text-slate-700"
            }`}
          >
            <Icon size={16} />
            <span className="flex-1 text-left text-sm">{item.label}</span>
            {item.shortcut && (
              <span className="text-xs text-slate-400">{item.shortcut}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * ElementToolbar - Floating toolbar that appears above selected element
 */
export function ElementToolbar({
  element,
  position,
  onUpdateElement,
  onShowContextMenu,
}) {
  if (!element || !position) return null;

  const isTextElement = element.type === "text" || element.type === "pedagogical_text";

  return (
    <div
      className="absolute z-40 flex items-center gap-1 bg-white rounded-lg shadow-lg border border-slate-200 p-1"
      style={{
        left: position.x,
        top: Math.max(8, position.y - 48),
        transform: "translateX(-50%)",
      }}
    >
      {isTextElement && (
        <>
          <button
            onClick={() => onUpdateElement("fontWeight", element.fontWeight === "bold" ? "normal" : "bold")}
            className={`p-1.5 rounded transition-colors ${
              element.fontWeight === "bold" ? "bg-slate-800 text-white" : "hover:bg-slate-100"
            }`}
            title="Gras (Ctrl+B)"
          >
            <Bold size={14} />
          </button>
          <button
            onClick={() => onUpdateElement("fontStyle", element.fontStyle === "italic" ? "normal" : "italic")}
            className={`p-1.5 rounded transition-colors ${
              element.fontStyle === "italic" ? "bg-slate-800 text-white" : "hover:bg-slate-100"
            }`}
            title="Italique (Ctrl+I)"
          >
            <Italic size={14} />
          </button>
          <button
            onClick={() => onUpdateElement("textDecoration", element.textDecoration === "line-through" ? "none" : "line-through")}
            className={`p-1.5 rounded transition-colors ${
              element.textDecoration === "line-through" ? "bg-slate-800 text-white" : "hover:bg-slate-100"
            }`}
            title="Barre"
          >
            <Strikethrough size={14} />
          </button>
          <div className="w-px h-5 bg-slate-200 mx-1" />
        </>
      )}
      
      <button
        onClick={(e) => onShowContextMenu({ x: e.clientX, y: e.clientY })}
        className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
        title="Plus d'options"
      >
        <Layers size={14} />
      </button>
    </div>
  );
}

export default ElementContextMenu;
